import { VictronImage } from '@components/display/images';
import { useAppSelector } from '@hooks/generic';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { alpha, Button, ButtonBase, Chip, Skeleton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { getDateFnsLocale } from '@utils/common';
import { format } from 'date-fns';
import { FormattedMessage } from 'react-intl';
import { Link, LinkProps } from 'react-router-dom';

type MediaStoryCardProps = {
    loading?: boolean;
    baseUrl?: string;
    slug: string;
    prefetchFn?: (slug: string) => void;
    image: {
        url: string;
        alt_text: string;
    };
    date?: string;
    title: string;
    link?: {
        text?: React.ReactNode;
        /**
         * In case it's to redirect to a URL
         */
        url?: string;
        /**
         * In case it's to do something other than just redirecting, i.e. opening a modal
         */
        fn?: (slug: string) => void;
        target?: LinkProps['target'];
    };
    draft?: boolean;
};

export const MediaStoryCard = ({
    loading,
    baseUrl,
    slug,
    prefetchFn,
    image,
    date,
    title,
    link,
    draft,
}: MediaStoryCardProps) => {
    const isReactLink = baseUrl?.startsWith('/app');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const Image = (
        <div className="relative w-full aspect-[calc(16/9)]">
            <VictronImage
                src={image.url}
                alt={image.alt_text}
                className="h-full w-full rounded-md object-cover transition-transform duration-300 ease-out-quad group-hover:scale-105"
            />
        </div>
    );

    const isLink = !!link && !link.fn;

    const currentLocale = useAppSelector((state) => state.translation.locale);
    const dateLocale = getDateFnsLocale(currentLocale);

    const componentProps =
        isMobile && link?.url
            ? {
                  component: 'a',
                  href: link.url,
                  target: link.target ?? '_blank',
              }
            : !isLink
            ? {
                  component: 'button',
                  onClick: () => link?.fn?.(slug),
              }
            : isReactLink
            ? {
                  component: Link,
                  to: `${baseUrl}${slug}`,
                  onMouseEnter: () => prefetchFn?.(slug),
                  target: link.target ?? '_blank',
              }
            : {
                  component: 'a',
                  href: `${baseUrl}${slug}`,
                  target: link.target ?? '_blank',
              };

    const CardContent = (
        <Stack direction="column" height="100%" width="100%" textAlign="left" className="group" position="relative">
            {loading ? (
                <Skeleton variant="rectangular" width="100%" className="rounded-md">
                    {Image}
                </Skeleton>
            ) : baseUrl ? (
                isReactLink ? (
                    <Link to={`${baseUrl}${slug}`} onMouseEnter={() => prefetchFn?.(slug)} target="_blank">
                        {Image}
                    </Link>
                ) : (
                    <a href={`${baseUrl}${slug}`} target="_blank">
                        {Image}
                    </a>
                )
            ) : (
                <div className="block w-full aspect-[calc(16/9)] p-0 bg-transparent">{Image}</div>
            )}
            {loading ? (
                <Skeleton className="mt-6">
                    <Typography>dd MMM, yyyy</Typography> {/* Placeholder for date text, no need for i18n */}
                </Skeleton>
            ) : (
                date && (
                    <Typography mt="24px" fontSize={14} fontWeight={500} lineHeight="19.2px" color={alpha('#000', 0.4)}>
                        {format(new Date(date), 'dd MMM yyyy', { locale: dateLocale })}
                    </Typography>
                )
            )}
            {loading ? (
                <Skeleton variant="text">
                    <Typography lineHeight="19.2px" className="line-clamp-2 md:line-clamp-3">
                        Loading title, which is very big... {/* Placeholder for title text, no need for i18n */}
                    </Typography>
                </Skeleton>
            ) : (
                <Typography fontSize={16} fontWeight={500} lineHeight="19.2px" className="line-clamp-2 md:line-clamp-3">
                    {loading ? <Skeleton /> : title}
                </Typography>
            )}
            {loading ? (
                <Skeleton>
                    <Typography>Read More</Typography> {/* Placeholder for link text, no need for i18n */}
                </Skeleton>
            ) : (
                link && (
                    <div className="mt-auto">
                        <Button
                            className="mt-[16px] p-0 font-medium"
                            variant="text"
                            endIcon={<ArrowForwardIcon />}
                            classes={{
                                endIcon: 'transition-transform duration-300 ease-out-quad group-hover:translate-x-1',
                            }}
                        >
                            {link?.text ?? <FormattedMessage id="common.actions.readMore" defaultMessage="Read more" />}
                        </Button>
                    </div>
                )
            )}
            {draft && (
                <Chip
                    color="signal-warning"
                    label={
                        <Typography fontSize={16} fontWeight={500} color="white">
                            <FormattedMessage id="common.status.draft" defaultMessage="Draft" />
                        </Typography>
                    }
                    className="absolute top-2 left-2"
                />
            )}
        </Stack>
    );

    if (loading || !link) {
        return CardContent;
    }

    return (
        <ButtonBase {...componentProps} focusRipple className="w-full h-full rounded-lg">
            {CardContent}
        </ButtonBase>
    );
};
